import { useEffect, useState, useCallback, useRef } from 'react';
import { Icon } from '../components/icons';
import { getCrmSocket, connectCrmSocket } from '../lib/socket';
import { primeAudio, play, notifyOs, type SoundPreset } from '../lib/sound';

interface ActivityRow {
  ts: number;
  type: 'loggedIn' | 'loggedOut' | 'joinedRoom' | 'leftRoom' | 'gameFinished';
  text: string;
}

const SETTINGS_KEY = 'bura_crm_notif_settings';

interface NotifSettings {
  enabled: boolean;
  login: boolean;
  joinRoom: boolean;
  leaveRoom: boolean;
  logout: boolean;
  gameFinished: boolean;
  osNotification: boolean; // fallback для свёрнутой вкладки
  volume: number; // 0..1
}

const DEFAULT_SETTINGS: NotifSettings = {
  enabled: true,
  login: true,
  joinRoom: true,
  leaveRoom: false, // leaveRoom чуть навязчиво, по умолчанию off
  logout: false,
  gameFinished: true,
  osNotification: false,
  volume: 0.18
};

function loadSettings(): NotifSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function NotificationsPage() {
  const [settings, setSettings] = useState<NotifSettings>(() => loadSettings());
  const [activity, setActivity] = useState<ActivityRow[]>([]);
  const [audioPrimed, setAudioPrimed] = useState(false);
  const activityRef = useRef<ActivityRow[]>([]);

  // Подписка на socket-события CRM namespace
  useEffect(() => {
    connectCrmSocket();
    const sock = getCrmSocket();
    if (!sock) return;

    const onConnect = () => sock.emit('crm:subscribe');

    const pushActivity = (type: ActivityRow['type'], text: string) => {
      const row: ActivityRow = { ts: Date.now(), type, text };
      const next = [row, ...activityRef.current].slice(0, 50);
      activityRef.current = next;
      setActivity(next);
    };

    const onLogin = (p: { nickname: string }) => {
      if (!settings.enabled || !settings.login) return;
      play('login');
      maybeOs(`🟢 ${p.nickname} зашёл`, `Юзер ${p.nickname} появился в лобби`);
      pushActivity('loggedIn', `🟢 ${p.nickname} зашёл в лобби`);
    };

    const onLogout = (p: { nickname: string }) => {
      if (!settings.enabled || !settings.logout) return;
      play('logout');
      maybeOs(`🔴 ${p.nickname} покинул платформу`, '');
      pushActivity('loggedOut', `🔴 ${p.nickname} вышел`);
    };

    const onJoined = (p: { nickname: string; roomName: string }) => {
      if (!settings.enabled || !settings.joinRoom) return;
      play('joinRoom');
      maybeOs(`🚪 ${p.nickname} в комнате "${p.roomName}"`, 'Ожидание игроков');
      pushActivity('joinedRoom', `🚪 ${p.nickname} → "${p.roomName}"`);
    };

    const onLeft = (p: { nickname: string; roomName: string }) => {
      if (!settings.enabled || !settings.leaveRoom) return;
      play('leaveRoom');
      maybeOs(`🚪 ${p.nickname} покинул "${p.roomName}"`, '');
      pushActivity('leftRoom', `🚪 ${p.nickname} ← "${p.roomName}"`);
    };

    const onGameFinished = (p: {
      roomName: string;
      reason: string;
      winners: Array<{ nickname: string; share: number }>;
      losers: Array<{ nickname: string; gamePoints: number }>;
    }) => {
      if (!settings.enabled || !settings.gameFinished) return;
      const preset: SoundPreset = p.winners.length > 0 ? 'gameWon' : 'gameLost';
      play(preset);
      const top = p.winners[0];
      const title = top
        ? `🏆 Партия: ${top.nickname} +${top.share}`
        : `🎲 Партия завершена в "${p.roomName}"`;
      const body = `${p.reason}\n${p.winners
        .map((w) => `🏆 ${w.nickname} +${w.share}`)
        .join('\n')}`;
      maybeOs(title, body);
      pushActivity(
        'gameFinished',
        `🎲 "${p.roomName}" → ${p.winners.length > 0 ? p.winners.map((w) => `${w.nickname} +${w.share}`).join(', ') : 'без победителей'}`
      );
    };

    function maybeOs(title: string, body: string) {
      if (settings.osNotification) notifyOs(title, body);
    }

    sock.on('connect', onConnect);
    sock.on('crm:hello', onConnect);
    sock.on('crm:lobby:loggedIn', onLogin);
    sock.on('crm:lobby:loggedOut', onLogout);
    sock.on('crm:lobby:joinedRoom', onJoined);
    sock.on('crm:lobby:leftRoom', onLeft);
    sock.on('crm:game:finished', onGameFinished);

    if (sock.connected) sock.emit('crm:subscribe');

    return () => {
      sock.off('connect', onConnect);
      sock.off('crm:hello', onConnect);
      sock.off('crm:lobby:loggedIn', onLogin);
      sock.off('crm:lobby:loggedOut', onLogout);
      sock.off('crm:lobby:joinedRoom', onJoined);
      sock.off('crm:lobby:leftRoom', onLeft);
      sock.off('crm:game:finished', onGameFinished);
    };
  }, [settings]);

  // Save settings
  useEffect(() => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch { /* ignore */ }
  }, [settings]);

  const toggleSound = useCallback(async () => {
    if (!audioPrimed) {
      await primeAudio();
      setAudioPrimed(true);
    }
    setSettings((s) => ({ ...s, enabled: !s.enabled }));
  }, [audioPrimed]);

  const setOpt = <K extends keyof NotifSettings>(k: K, v: NotifSettings[K]) =>
    setSettings((s) => ({ ...s, [k]: v }));

  const testSound = async (preset: SoundPreset) => {
    if (!audioPrimed) {
      await primeAudio();
      setAudioPrimed(true);
    }
    play(preset);
  };

  return (
    <div className="anim-fade space-y-6">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Уведомления</h1>
          <p className="text-sm text-stone-400 mt-1">
            Звуковые оповещения о событиях в лобби и об итогах партий.
            Чтобы звук играл в свёрнутой вкладке — браузер должен был хотя бы раз
            взаимодействовать со страницей (нажми «Включить звук» ниже).
          </p>
        </div>
        <button
          onClick={toggleSound}
          className={`btn-modern ${settings.enabled ? 'bg-emerald-600 hover:bg-emerald-500 text-white' : 'bg-stone-700 hover:bg-stone-600 text-stone-200'}`}
        >
          <Icon.Bell size={18} />
          {settings.enabled ? 'Звук включён' : 'Звук выключен'}
        </button>
      </header>

      {/* Settings */}
      <section className="glass rounded-2xl p-5 space-y-4">
        <h2 className="text-sm font-medium text-stone-300">Какие события озвучивать</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ToggleRow
            label="🟢 Юзер зашёл в лобби"
            checked={settings.login}
            disabled={!settings.enabled}
            onChange={(v) => setOpt('login', v)}
            onTest={() => testSound('login')}
          />
          <ToggleRow
            label="🚪 Юзер вошёл в комнату"
            checked={settings.joinRoom}
            disabled={!settings.enabled}
            onChange={(v) => setOpt('joinRoom', v)}
            onTest={() => testSound('joinRoom')}
          />
          <ToggleRow
            label="🚪 Юзер вышел из комнаты"
            checked={settings.leaveRoom}
            disabled={!settings.enabled}
            onChange={(v) => setOpt('leaveRoom', v)}
            onTest={() => testSound('leaveRoom')}
          />
          <ToggleRow
            label="🔴 Юзер покинул платформу"
            checked={settings.logout}
            disabled={!settings.enabled}
            onChange={(v) => setOpt('logout', v)}
            onTest={() => testSound('logout')}
          />
          <ToggleRow
            label="🎲 Партия завершена (с результатами)"
            checked={settings.gameFinished}
            disabled={!settings.enabled}
            onChange={(v) => setOpt('gameFinished', v)}
            onTest={() => testSound('gameWon')}
            fullWidth
          />
        </div>

        <div className="pt-2 border-t border-stone-700/50 space-y-3">
          <label className="flex items-center justify-between gap-3 text-sm">
            <span className="text-stone-300">
              Дополнительно показывать OS-уведомление
              <span className="block text-xs text-stone-500 mt-0.5">
                Работает в свёрнутой вкладке (через браузерный Notification API)
              </span>
            </span>
            <input
              type="checkbox"
              className="accent-amber-500 w-5 h-5"
              checked={settings.osNotification}
              onChange={(e) => setOpt('osNotification', e.target.checked)}
            />
          </label>
          <label className="flex items-center justify-between gap-3 text-sm">
            <span className="text-stone-300">Громкость</span>
            <input
              type="range"
              min={0}
              max={0.5}
              step={0.01}
              value={settings.volume}
              onChange={(e) => setOpt('volume', Number(e.target.value))}
              className="w-40 accent-amber-500"
            />
          </label>
        </div>
      </section>

      {/* Live feed */}
      <section className="glass rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-stone-300">Лента событий</h2>
          <span className="text-xs text-stone-500">
            последние {activity.length}
          </span>
        </div>
        {activity.length === 0 ? (
          <div className="text-sm text-stone-500 py-8 text-center">
            Событий пока нет. Открой лобби <span className="font-mono">buraclub.online</span> в
            другой вкладке и залогинься — увидишь событие здесь.
          </div>
        ) : (
          <ul className="space-y-1.5 max-h-96 overflow-y-auto">
            {activity.map((row, i) => (
              <li
                key={`${row.ts}-${i}`}
                className="flex items-center gap-3 text-sm py-1.5 px-3 rounded-lg hover:bg-stone-800/40"
              >
                <span className="text-xs text-stone-500 font-mono w-14">
                  {new Date(row.ts).toLocaleTimeString('ru-RU', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  })}
                </span>
                <span className="flex-1 text-stone-200">{row.text}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function ToggleRow({
  label,
  checked,
  disabled,
  onChange,
  onTest,
  fullWidth
}: {
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
  onTest: () => void;
  fullWidth?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between gap-3 p-3 rounded-xl bg-stone-800/40 ${
        fullWidth ? 'sm:col-span-2' : ''
      }`}
    >
      <span className={`text-sm ${disabled ? 'text-stone-500' : 'text-stone-200'}`}>{label}</span>
      <div className="flex items-center gap-2">
        <button
          onClick={onTest}
          className="text-xs px-2 py-1 rounded-md bg-stone-700 hover:bg-stone-600 text-stone-200"
          title="Послушать звук"
        >
          🔊 тест
        </button>
        <input
          type="checkbox"
          className="accent-amber-500 w-5 h-5"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
      </div>
    </div>
  );
}